import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '@gray/domain/http/api.service';

/**
 * The student's planner, operated by their coach. Mirrors the student's own plan endpoints, so the
 * board behaves the same whoever is editing; only the path and the guard differ.
 *
 * The panel names the period it is showing on every call — no header travels with the request.
 */
@Injectable({providedIn: 'root'})
export class CoachStudentPlanService {

  constructor(
    private api: ApiService,
  ) {
  }

  private prefix(studentId: number): string {
    return `/coach/students/${studentId}/plans`;
  }

  private periodQuery(periodId?: number | null, extra = ''): string {
    const parts = [periodId ? `periodId=${periodId}` : '', extra].filter(part => !!part);
    return parts.length ? `?${parts.join('&')}` : '';
  }

  getWeeks(studentId: number, periodId: number): Observable<any> {
    return this.api.get(`${this.prefix(studentId)}/weeks-by-period/${periodId}`);
  }

  getWeekItems(studentId: number, weekId: number, periodId?: number | null): Observable<any> {
    return this.api.get(`${this.prefix(studentId)}/weeks/${weekId}/items`, this.periodQuery(periodId));
  }

  createWeekItem(studentId: number, weekId: number, data: object, periodId?: number | null): Observable<any> {
    return this.api.post(`${this.prefix(studentId)}/weeks/${weekId}/items${this.periodQuery(periodId)}`, data);
  }

  deleteWeekItem(studentId: number, weekItemId: number, periodId?: number | null): Observable<any> {
    return this.api.delete(`${this.prefix(studentId)}/week-items/${weekItemId}${this.periodQuery(periodId)}`);
  }

  copyWeekItem(studentId: number, weekItemId: number, data: object, periodId?: number | null): Observable<any> {
    return this.api.post(`${this.prefix(studentId)}/week-items/${weekItemId}/copy${this.periodQuery(periodId)}`, data);
  }

  repeatWeekItem(studentId: number, weekItemId: number, periodId?: number | null): Observable<any> {
    return this.api.post(`${this.prefix(studentId)}/week-items/${weekItemId}/repeat${this.periodQuery(periodId)}`, {});
  }

  transferWeekItem(
    studentId: number, weekId: number, itemId: number, toWeekId: number, periodId?: number | null,
  ): Observable<any> {
    return this.api.post(
      `${this.prefix(studentId)}/weeks/${weekId}/items/${itemId}/transfer${this.periodQuery(periodId)}`,
      {toWeekId},
    );
  }

  completeWeekItem(studentId: number, weekId: number, itemId: number, periodId?: number | null): Observable<any> {
    return this.api.post(
      `${this.prefix(studentId)}/weeks/${weekId}/items/${itemId}/complete${this.periodQuery(periodId)}`, {},
    );
  }

  uncompleteWeekItem(studentId: number, weekId: number, itemId: number, periodId?: number | null): Observable<any> {
    return this.api.delete(
      `${this.prefix(studentId)}/weeks/${weekId}/items/${itemId}/complete${this.periodQuery(periodId)}`,
    );
  }

  assignItemToDay(
    studentId: number, weekId: number, itemId: number, data: object, periodId?: number | null,
  ): Observable<any> {
    return this.api.post(
      `${this.prefix(studentId)}/weeks/${weekId}/items/${itemId}/assign${this.periodQuery(periodId)}`, data,
    );
  }

  deleteAssignment(studentId: number, weekId: number, itemId: number, periodId?: number | null): Observable<any> {
    return this.api.delete(
      `${this.prefix(studentId)}/weeks/${weekId}/items/${itemId}/assign${this.periodQuery(periodId)}`,
    );
  }

  /** Searches the student's own week items; needs a lesson or a topic filter. */
  searchWeekItems(studentId: number, periodId: number, queryString = ''): Observable<any> {
    return this.api.get(`${this.prefix(studentId)}/week-items/search-by-period/${periodId}`, queryString);
  }

  // region Ready-made plans
  getPlanCategories(studentId: number, periodId?: number | null): Observable<any> {
    return this.api.get(`${this.prefix(studentId)}/catalog/categories`, this.periodQuery(periodId));
  }

  getAppliedPlans(studentId: number, periodId?: number | null): Observable<any> {
    return this.api.get(`${this.prefix(studentId)}/catalog/applied`, this.periodQuery(periodId));
  }

  getPlanDetails(studentId: number, planId: number, periodId?: number | null): Observable<any> {
    return this.api.get(`${this.prefix(studentId)}/catalog/${planId}/details`, this.periodQuery(periodId));
  }

  importPlan(studentId: number, planId: number, data: object, periodId?: number | null): Observable<any> {
    return this.api.post(`${this.prefix(studentId)}/catalog/${planId}/import${this.periodQuery(periodId)}`, data);
  }

  rollbackPlan(studentId: number, planId: number, periodId?: number | null): Observable<any> {
    return this.api.delete(`${this.prefix(studentId)}/catalog/${planId}/rollback${this.periodQuery(periodId)}`);
  }
  // endregion

}
